import { MotionGlobalConfig } from "framer-motion";
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useSearchParams, useLocation } from "react-router-dom";
import * as VS from "./services/ValidatorService";
import * as TerminalService from "./services/data/TerminalService";
import * as PatientService from "./services/data/PatientService";
import * as Helper from "./_Helper";
import * as Const from "./_Const";
import useAxios from "./services/useAxios";
import { useLocalStorage, useSessionStorage, useStorage } from "./hooks/useStorage";
import useTimeout from "./hooks/useTimeout";
import useDetectScreenOff from "./hooks/useDetectScreenOff";
import useDarkMode from "./hooks/useDarkMode";
import useErrorState from "./hooks/useErrorState";
// import useBeforeUnload from "./hooks/useBeforeUnload";

const FormContext = React.createContext();

export function useFormCtx() {
  return useContext(FormContext);
}

const registrationData = [
  {
    stepIndex: 0,
    route: "",
    title: "Úvodní obrazovka",
    subtitle: "",
    showNext: false,
    showBack: false,
    nextTitle: "",
    showNextIcon: false,
    showSteps: false,
  },
  {
    stepIndex: Const.REG_01_INDEX,
    route: Const.REG_01,
    title: "Důvod návštěvy",
    subtitle: "Zvolte důvod návštěvy",
    showNext: false,
    showBack: true,
    nextRoute: Const.REG_02,
    nextTitle: "",
    showNextIcon: false,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_02_INDEX,
    route: Const.REG_02,
    title: "Osobní údaje",
    subtitle: "Vyplňte níže uvedené údaje",
    showNext: true,
    showBack: false,
    nextTitle: "Registrovat",
    nextRoute: Const.REG_SUMMARY,
    showNextIcon: false,
    showSteps: true,
  },
];

const registrationDataRpi = [
  {
    stepIndex: 0,
    route: "",
    title: "Úvodní obrazovka",
    subtitle: "",
    showNext: false,
    showBack: false,
    nextTitle: "",
    showNextIcon: false,
    showSteps: false,
  },
  {
    stepIndex: Const.REG_RPI_01_INDEX,
    route: Const.REG_01,
    title: "Důvod návštěvy",
    subtitle: "Zvolte důvod návštěvy",
    showNext: false,
    showBack: true,
    nextRoute: Const.REG_INUSRANCE,
    nextTitle: "",
    showNextIcon: false,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_RPI_INUSRANCE_COMPANY_INDEX,
    route: Const.REG_INUSRANCE,
    title: "Osobní údaje - pojišťovna",
    subtitle: "Vyberte prosím Vaši pojišťovnu",
    showNext: false,
    showBack: false,
    nextRoute: Const.REG_PERSONAL_NUMBER,
    nextTitle: "",
    showNextIcon: false,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_RPI_PERSONAL_NUMBER_INDEX,
    route: Const.REG_PERSONAL_NUMBER,
    title: "Osobní údaje - rodné číslo",
    subtitle: "Vyplňte prosím Vaše rodné číslo",
    showNext: true,
    showBack: false,
    nextTitle: "Další stránka",
    nextRoute: Const.REG_LAST_NAME,
    showNextIcon: true,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_RPI_LAST_NAME_INDEX,
    route: Const.REG_LAST_NAME,
    title: "Osobní údaje - příjmení",
    subtitle: "Vyplňte prosím Vaše příjmení",
    showNext: true,
    showBack: false,
    nextTitle: "Další stránka",
    nextRoute: Const.REG_NAME,
    showNextIcon: true,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_RPI_NAME_INDEX,
    route: Const.REG_NAME,
    title: "Osobní údaje - jméno",
    subtitle: "Vyplňte prosím Vaše jméno",
    showNext: true,
    showBack: false,
    nextTitle: "Registrovat se do systému",
    nextRoute: Const.REG_SUMMARY,
    showNextIcon: false,
    showSteps: true,
  },
];

const defaultState = {
  queueId: 0,
  queueName: "",
  queueFullName: "",
  personalNumber: "",
  lastName: "",
  firstName: "",
  email: "",
  insurance: "",
  errors: { queueId: 0, personalNumber: "", lastName: "", firstName: "", email: "", insurance: "" },
  skippedValidation: { queueId: false, personalNumber: false, lastName: false, firstName: false, email: false, insurance: false },
};

const defaultWelcomePageState = {
  terminalUID: "",
  mobileAppTitle: "",
  mobileAppSubTitle: "",
  isMobileAppRegistrationEnabled: false,
  isClosed: false,
  closedTitle: "",
  closedSubTitle: "",
  keyNumber: "",
  isPersonalNumberValidationSkipEnabled: false,
};

const defaultNavigateOptions = {
  replace: true,
};

export function FormProvider({ children }) {
  const isTerminal = Const.isTerminal();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { instance } = useAxios();
  const navigate = useNavigate();

  const [logText, setLogText] = useState("");

  const [isTaskRunning, setTaskRunning] = useState(false);
  const [isDebugMode, setDebugMode] = useState(false);
  const [isDebugModeBackBtnClickable, setIsDebugModeBackBtnClickable] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pageRendered, setPageRendered] = useState(false);
  const [state, setState] = useState(defaultState);

  //slouzi pro nastaveni dark light theme musi se alespon volat i kdy handlovani toggleTheme je v DebugPage
  const [isDarkMode, toggleTheme, setThemeByValue] = useDarkMode();
  const [isInErrorState, setErrorState] = useErrorState();

  const [storageStartTime, setStorageStartTime, removeStorageStartTime] = useSessionStorage(Const.SS_START_TIME, 0);
  const [storageUid, setStorageUid, removeStorageUid] = useStorage(Const.SS_UID, "", isTerminal ? window.localStorage : window.sessionStorage);
  const [storageRegType, setStorageRegType, removeStorageRegType] = useStorage(Const.SS_REG_TYPE, "", isTerminal ? window.localStorage : window.sessionStorage);
  const [firstName, setFirstName, removeFirstName] = useLocalStorage(Const.LS_FIRST_NAME, "");
  const [lastName, setLastName, removeLastName] = useLocalStorage(Const.LS_LAST_NAME, "");
  const [personalNumber, setPersonalNumber, removePersonalNumber] = useLocalStorage(Const.LS_PERSONAL_NUMBER, "");
  const [insurance, setInsurance, removeInsurance] = useLocalStorage(Const.LS_INSURANCE, "");
  const [email, setEmail, removeEmail] = useLocalStorage(Const.LS_EMAIL, "");

  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [welcomePageState, setWelcomePageState] = useState(defaultWelcomePageState);
  const [currentBtnPageIndex, setCurrentBtnPageIndex] = useState(0);
  const [selectedButtonId, setSelectedButtonId] = useState(0);
  const [lastSelectedButtonId, setLastSelectedButtonId] = useState(0);
  const [qTitle, setQTitle] = useState("");
  const [pageData, setPageData] = useState(isTerminal ? registrationDataRpi : registrationData);
  const [buttonsData, setButtonsData] = useState(null);
  const [lastError, setLastError] = useState(0);
  const [isAnimationDisabled, setAnimationDisabled, removeAnimationDisabled] = useLocalStorage(Const.LS_ANIMATION_DISABLED, false);

  const { clear: clearTimer, reset: resetTimer } = useTimeout(() => navigateAfterTimeout(), Const.PAGE_EXPIRED_TIME);
  const { clear: clearTerminalSummaryTimer, reset: resetTerminalSummaryTimer } = useTimeout(
    () => navigateAfterRegSummaryIntervalTimeout(),
    Const.PAGE_REG_SUMMARY_TERMINAL_TIME
  );

  //na produkci vzdy je timer povolen
  let isTimersEnabled = Const.isProduction() || Const.isPreproduction();

  //handlovani animaci
  setAnimationStateDisabled(isAnimationDisabled);

  //v debug zakaz timer nebo si ho povol na test
  if (Const.isDebugMode()) {
    isTimersEnabled = false;
  }

  //nenili timer povolen smaz ho napr v debug
  if (!isTimersEnabled) {
    clearTimer();
    clearTerminalSummaryTimer();
  }

  const startTime = performance.now();

  useEffect(() => {
    async function scopedLoadData() {
      postPoneExpiredInterval();

      setLoaded(false);
      setPageRendered(false);
      setRegType();
      setPatientFromLocalStorage();

      const result = await populateData(getUid());
      if (result) {
        const pages = result.pages;

        if (isTerminal) {
          //nacist dark mode z jsonu na serveru
          //aby to fungovalo musi byt delay
          setTimeout(() => {
            setThemeByValue(result.enableDarkTheme);
            //logd("enableDarkTheme:" + result.enableDarkTheme);
          }, 500);
        }

        let wpState = welcomePageState;

        wpState.terminalUID = result.terminalUid;
        wpState.isClosed = result.isClosed;
        wpState.mobileAppSubTitle = result.mobileAppSubTitle;
        wpState.mobileAppTitle = result.mobileAppTitle;
        wpState.isMobileAppRegistrationEnabled = result.isMobileAppRegistrationEnabled;
        wpState.closedTitle = result.closedPageTitle;
        wpState.closedSubTitle = result.closedPageText;
        wpState.keyNumber = result.keyNumber;
        wpState.isPersonalNumberValidationSkipEnabled = result.isPersonalNumberValidationSkipEnabled;
        setWelcomePageState(wpState);
        //kdykoliv v ramci push notifikace muze prijit terminal close - pomoci reload page z linuxu,
        // v pripade ze budu na jine nez home strance dojde jen k znovunacteni dat ale ne presmerovani na home page a stav close
        if (isTerminal) {
          handleIsTerminalClosed(result.isClosed);
        }
        //logd("terminal data loaded", result);

        setButtonsData(pages);

        handleSplashClose();
      }
    }
    scopedLoadData();
  }, []);

  const isScreenOff = useDetectScreenOff();
  if (pageRendered) {
    //logd("pageRendered true call handleIntervalAfterScreenOn");
    handleIntervalAfterScreenOn();
  }

  async function populateData(uid) {
    try {
      let { data: item } = await TerminalService.getData(instance, uid);
      return item;
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      }
    }
  }

  async function setDarkMode(isDark) {
    try {
      setTaskRunning(true);
      await TerminalService.setDarkMode(instance, getUid(), isDark);
      logd("setDarkMode isDark:" + isDark);
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function runRestartOrShutdown(operation) {
    try {
      setTaskRunning(true);
      //v debug neprovadet, napriklad mam web http://localhost:3000/?regType=2&uid=fbs7lgrsrdewz3u0
      //napojen na realne sve zarizeni, zbytecne mu budu ve vypnutem stavu kumulovat restart, nebo shutdown a po zapnuti se to bude porad provadet
      if (!Const.isDebugMode()) {
        logd("runRestartOrShutdown operation:" + operation);
        await TerminalService.runRestartOrShutdown(instance, getUid(), operation);
      } else {
        logd("fake runRestartOrShutdown operation:" + operation);
      }
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function validate(index, skipValidation = false) {
    const page = getPageData(index);
    logd("validate", page);

    //na tlacitko registrovat zkontrolavat formular na spravne strance v ramci contextu
    const registrationPageIndex = Const.isTerminal() ? Const.REG_RPI_NAME_INDEX : Const.REG_02_INDEX;
    if (index === registrationPageIndex) {
      if (VS.handleSubmit(null, state, validateFormItem, null)) {
        try {
          setTaskRunning(true);
          savePatientToLocalStorage(state);
          navigateNext(page.nextRoute);
          await PatientService.register(instance, getUid(), state.queueId, getRegType(), setViewToModel(state));
          // await PatientService.x(instance, req);
        } catch (ex) {
          loge(ex, ex?.response);
          setLastError(ex?.response?.status);
          if (ex?.response?.status === 400) {
            navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
          } else if (ex?.response?.status !== 409) {
            navigateToErrorPage(Const.PAGE_ERROR);
          }
        } finally {
          setTaskRunning(false);
          //nastartuj timer
          resetTerminalSummaryTimer();
        }
      }
    } else {
      //v terminalu validovat kazdou stranku ktera je samostane a ne a z na konci pred registraci
      if (isTerminal) {
        let name = "";
        let val;
        let validate = false;

        switch (index) {
          case Const.REG_RPI_PERSONAL_NUMBER_INDEX:
            name = "personalNumber";
            val = state.personalNumber;
            validate = true;
            break;
          case Const.REG_RPI_LAST_NAME_INDEX:
            name = "lastName";
            val = state.lastName;
            validate = true;
            break;
        }

        if (validate) {
          validateFormItem(name, val, skipValidation);
        }
        const haveError = state.errors[name];
        //neni li chyba nebo je vypnuta validace - tak na dalsi stranku
        //if (!haveError || !validate) {
        if (!haveError) {
          navigateNext(page.nextRoute);
        }
      } else {
        navigateNext(page.nextRoute);
      }
    }
  }

  async function sendLogToApi() {
    logd("sendLogToApi");

    //pridati aktualniho stavu aplikace
    const dataToSend = `${logText}[${formatTime()}] APP STATE: ${formatLine(state)}\n;`;

    // await new Promise((resolve) =>
    //   setTimeout(async () => {
    let isOk = false;
    if (isTerminal) {
      try {
        setTaskRunning(true);
        await TerminalService.addLog(instance, getUid(), dataToSend);
        isOk = true;
      } catch (ex) {
        loge(ex, ex?.response);
      } finally {
        setTaskRunning(false);
        if (isOk) {
          logd(`Data úspěšně odeslána do API, log bude smazán za ${Const.LOG_DELETE_INTERVAL / 1000}s`);

          setTimeout(() => {
            setLogText("");
          }, Const.LOG_DELETE_INTERVAL);
        }
      }
    }
    //   resolve(isOk);
    // }, Const.LOG_DELETE_INTERVAL)
    // );
  }

  function handleIsTerminalClosed(isClosed) {
    //kdykoliv v ramci push notifikace muze prijit terminal close - pomoci reload page z linuxu,
    // v pripade ze budu na jine nez home strance dojde jen k znovunacteni dat ale ne presmerovani na home page a stav close
    //logd(isTerminal, isClosed, location.pathname);
    if (isTerminal && isClosed && Const.getIndexForPage(location.pathname) !== Const.HOME_INDEX) {
      logd("Need to go to home after close terminal notification");

      navigateHome();
    }
  }

  function handleDebug() {
    //neni li jeste debug mode a meni se na true tak pockej 500 ms na zabraneni kliknuti na talcitko zpet ihned po zobrazeni debug talcitka = debouncing
    if (!isDebugMode) {
      //logd("settimout");
      setTimeout(() => {
        setIsDebugModeBackBtnClickable(true);
        //logd("end");
      }, 500);
    } else {
      setIsDebugModeBackBtnClickable(false);
    }
    setDebugMode(!isDebugMode);
  }

  function postPoneExpiredInterval() {
    setStorageStartTime(Date.now());
  }

  function handleIntervalAfterScreenOn() {
    //tato polozka jen kdy se jedna o telefon ne web terminal

    if (!isTerminal) {
      if (isTimersEnabled || Const.isProduction()) {
        if (!isScreenOff && storageStartTime > 0) {
          const timeElapsed = Date.now() - storageStartTime;
          const isExpired = timeElapsed > Const.PAGE_EXPIRED_TIME ? true : false;
          //musi byt console.log jinak hazi chybu protoze se jeste nevytvoril cely objekt
          //console.log("chekcInterval: isExpired:" + isExpired + " timeElapsed:" + timeElapsed / 1000 + " interval:" + Const.PAGE_EXPIRED_TIME);

          if (isExpired && storageStartTime > 0) {
            setPageRendered(false);
            //musim pouzit debounce 500ms aby se vse stihlo bez chyby
            setTimeout(() => {
              logd("chekcInterval is expired navigate out");
              navigateAfterTimeout();
            }, 500);
          }
        }
      }
    }
  }

  function navigateAfterRegSummaryIntervalTimeout() {
    logd("navigateAfterRegSummaryIntervalTimeout expired");

    //resit vyprseni intervalu jen na REG_RPI_SUMMARY_INDEX a jen pro terminal po 15s
    if (isTerminal && Const.getIndexForPage(location.pathname) === Const.REG_RPI_SUMMARY_INDEX) {
      logd("navigateAfterRegSummaryIntervalTimeout", selectedPageIndex, isTerminal);
      navigateHome();
    }
  }

  function navigateAfterTimeout() {
    if (isTerminal) {
      navigateHome();
    } else {
      navigateToErrorPage(Const.PAGE_ERROR_PAGE_EXPIRED);
    }
  }

  function navigateToServicePage(route) {
    if (isTerminal) {
      resetTimer();
      //resetTerminalSummaryTimer();
      postPoneExpiredInterval();
      navigate(route, defaultNavigateOptions);
    }
  }

  function navigateToErrorPage(page) {
    clean();
    navigate(page, defaultNavigateOptions);
  }

  function clean() {
    //setStorageStartTime(0);
    clearTimer();
    clearTerminalSummaryTimer();
    setLastError("");
    if (!isTerminal) {
      removeStorageUid();
      removeStorageRegType();
    }
  }

  function resetToNextPatient() {
    if (isTerminal) {
      setState(defaultState);
    }
  }

  function getUid() {
    //debugger;
    let uid;
    try {
      //nejdriv se podivam do ulr zda tam neni uid
      uid = searchParams.get("uid");
      //  logd(uid, storageUid);

      //kdyz neni prazdne tak ho ulozim
      if (!VS.isEmpty(uid)) {
        //logd("ukladam uid: " + uid);
        setStorageUid(Helper.encrypt(uid));
      }

      //v pripade ze uzivatel refreshnul page a ztratili se info o terminalu na jine strance nez home tak nacti z sessionStorage
      // jestli refresnul stranku na home tak ma smulu bez tak na hlavni strance ma searchParams uid
      if (VS.isEmpty(uid) && !VS.isEmpty(storageUid) && location?.pathname !== Const.HOME) {
        uid = Helper.decrypt(storageUid);
        logd("Nacitam UID ze storage", uid);
      }
    } catch (error) {
      loge("Chyba při UID e:" + error);
    }

    return uid;
  }

  function getRegType() {
    let regType;
    try {
      //nejdriv se podivam do ulr zda tam neni uid
      regType = searchParams.get("regType");

      //v pripade ze uzivatel refreshnul page a ztratili se info o terminalu na jine strance nez home tak nacti z sessionStorage
      // jestli refresnul stranku na home tak ma smulu bez tak na hlavni strance ma searchParams uid
      if (VS.isEmpty(regType) && !VS.isEmpty(storageRegType) && location?.pathname !== Const.HOME) {
        regType = Helper.decrypt(storageRegType);
        logd("Nacitam regType ze storage");
      }
    } catch (error) {
      loge("Chyba při getRegType e:" + error);
    }

    return regType;
  }

  function setRegType() {
    let regType;
    try {
      //nejdriv se podivam do ulr zda tam neni uid
      regType = searchParams.get("regType");

      //kdyz neni prazdne tak ho ulozim
      if (!VS.isEmpty(regType)) {
        setStorageRegType(Helper.encrypt(regType));
      }
    } catch (error) {
      loge("Chyba při setRegType e:" + error);
    }

    return regType;
  }

  function savePatientToLocalStorage(item) {
    try {
      if (item && !isTerminal) {
        setLastName(Helper.encrypt(item.lastName));
        setFirstName(Helper.encrypt(item.firstName));
        setInsurance(Helper.encrypt(item.insurance));
        setEmail(Helper.encrypt(item.email));
        setPersonalNumber(Helper.encrypt(item.personalNumber));
      }
    } catch (error) {
      loge("Chyba při ukládání dat pacienta do local storage e:" + error);
    }
  }

  function setPatientFromLocalStorage() {
    try {
      if (defaultState && !isTerminal) {
        defaultState.lastName = Helper.decrypt(lastName);
        defaultState.firstName = Helper.decrypt(firstName);
        defaultState.insurance = Helper.decrypt(insurance);
        defaultState.email = Helper.decrypt(email);
        defaultState.personalNumber = Helper.decrypt(personalNumber);
        setState(defaultState);
      }
    } catch (error) {
      loge("Chyba při získávání dat pacienta z local storage e:" + error);
    }
  }

  function setViewToModel(data) {
    let personalNumber = data.personalNumber;
    //jeli skipnuta validace RC musi se nastavit unikatni identifikator aby jich na serveru mohlo byt vice
    if (data?.skippedValidation["personalNumber"] ?? false) {
      personalNumber = Helper.getRandomUID(7, true);
    }
    let vm = {
      name: data?.firstName?.toUpperCase(),
      surname: data?.lastName?.toUpperCase(),
      insuranceCompany: data.insurance,
      personalID: personalNumber?.toUpperCase(),
      email: data.email,
    };

    return vm;
  }

  function validateFormItem(name, value, skipValidation = false) {
    let errors = state.errors;
    let skippedValidation = state.skippedValidation;
    //debugger;
    //let localState = { ...state };
    switch (name) {
      case "lastName":
        errors[name] = VS.required(value, "Příjmení");
        break;
      case "firstName":
        errors[name] = VS.required(value, "Jméno");
        break;
      case "insurance":
        errors[name] = VS.requiredNotZero(value, "Pojišťovna");
        break;
      case "email":
        //kontrola emailu jen kdyz je vyplnen
        if (value) {
          errors[name] = VS.validateEmail(value, "E-mail");
        } else {
          errors[name] = "";
        }
        break;
      case "personalNumber":
        if (value) {
          if (value?.length > 10) {
            value = value.substring(0, 10);
          }
          errors[name] = VS.validatePersonalNumber(value);

          //pro test abych nemusel porad opisovat rc
          if (value === "888") {
            errors[name] = "";
          }
        } else {
          errors[name] = VS.required(value, "Rodné číslo", "");
        }

        //musim vzdy nastavit zda je nebo neni skip do state
        skippedValidation[name] = skipValidation;

        if (skipValidation) {
          errors[name] = "";
          value = "";
        }

        break;
    }
    //logd(value, localState.personalNumber, state.personalNumber);

    // logd(errors);

    //zobrazeni error bg pri chybe
    if (isTerminal) {
      if (isObjEmpty(errors)) {
        setErrorState(false);
      } else {
        setErrorState(true);
      }
    }

    setState({ ...state, errors, skippedValidation, [name]: value });
  }

  function isObjEmpty(obj) {
    let isEmtpy = true;
    for (var prop in obj) {
      const val = obj[prop];
      isEmtpy = VS.isEmpty(val);
      //logd(prop, val, isEmtpy);
      if (!isEmtpy) {
        return false;
      }
    }

    return isEmtpy;
  }

  function handleInput(e) {
    const { name, value } = e.target;
    setData(name, value);
  }

  function handleInputWithKeyboard(name, value) {
    setData(name, value);
  }

  //v pripade ze uzivatel klikne na tlacitko zpet v browseru a pak se vrati muze byt jiz prazdny state v tom pripade je dobre udelat nejakou akci
  function isEmptyState() {
    //staci porovnat tyto hodnoty
    if (state && state.queueId === 0) {
      // && VS.isEmpty(state.lastName)) {

      return true;
    }
    return false;
  }

  function setData(input, value, input2 = null, value2 = null, input3 = null, value3 = null, validateItem = true) {
    if (validateItem) {
      validateFormItem(input, value);
    }

    const localState = { ...state };
    localState[input] = value;
    if (input2 && value2) {
      localState[input2] = value2;
    }
    if (input3 && value3) {
      localState[input3] = value3;
    }

    setState(localState);
  }

  function navigateNext(route) {
    resetTimer();
    navigateToServicePage();
    postPoneExpiredInterval();
    setDebugMode(false);
    setSelectedPageIndex(Const.getIndexForPage(route));
    navigate(route, defaultNavigateOptions);
  }

  function navigateNextInsuranceButton(item, index) {
    const page = getPageData(index);
    logd("navigateNextInsuranceButton", item, index);
    setData("insurance", item.value);
    navigateNext(page.nextRoute);
  }

  function navigateNextButtons(item, index) {
    logd("navigateNextButtons", item, index);
    //debugger;
    //poznamenat pro tlacitko zpet
    if (selectedButtonId >= 0) {
      setLastSelectedButtonId(selectedButtonId);
    }

    if (item) {
      setSelectedButtonId(item.id);
    }

    if (item && item.queueId === 0) {
      const index = currentBtnPageIndex + 1;
      setCurrentBtnPageIndex(index > 2 ? 2 : index);
      setQTitle(getQueueTitle(item.text));
    } else {
      logd("test", getQueueTitle(item.text), item.text, item.queueId);

      setData("queueFullName", getQueueTitle(item.text), "queueName", item.text, "queueId", item.queueId, false);
      setQTitle("");
      setCurrentBtnPageIndex(0);
      setSelectedButtonId(0);
      validate(index);
    }
  }

  function getQueueTitle(title) {
    if (qTitle) {
      title = qTitle + " > " + title;
    }
    return title;
  }

  function replaceLastQueueTitleSegment() {
    let title = qTitle;
    if (title && title.length > 0) {
      const lastIndex = title.lastIndexOf(" > ");
      if (lastIndex > 0) {
        title = title.substring(0, lastIndex);
      } else {
        title = ""; //prvni segment neobsahuje > tak ze to jen smazu
      }
    }
    return title;
  }

  function navigateBackButtons() {
    const index = currentBtnPageIndex - 1;

    if (lastSelectedButtonId > 0) {
      setSelectedButtonId(lastSelectedButtonId);
    }
    setCurrentBtnPageIndex(index < 0 ? 0 : index);
    setQTitle(replaceLastQueueTitleSegment());
  }

  function showBackButton() {
    return currentBtnPageIndex == 0 ? false : true;
  }

  function navigateHome(uid = "") {
    //debugger;
    setLastError(0);
    resetToNextPatient();
    const passedUid = uid;
    const localUid = getUid();
    setSelectedPageIndex(Const.HOME_INDEX);
    logd(passedUid, localUid);

    searchParams.set("uid", !VS.isEmpty(uid) ? uid : getUid());
    setSearchParams(searchParams);
    if (!isTerminal) {
      resetTimer();
      navigateToServicePage();
      postPoneExpiredInterval();
    }

    navigate(
      {
        pathname: Const.HOME,
        search: searchParams.toString(),
      },
      defaultNavigateOptions
    );
  }

  function getPageData(index) {
    // logd("getPageData:", pageData);
    return pageData[index];
  }

  function getButtonsData() {
    return buttonsData;
  }

  function handleSplashClose() {
    const loadedTime = end();
    //logd(loadedTime);
    if (loadedTime < Const.SPLASH_TIME) {
      const splashTimeLeft = (Const.SPLASH_TIME - loadedTime) * 1000;
      //logd(splashTimeLeft);

      const timer = setTimeout(() => {
        //logd("end");
        setLoaded(true);
        setPageRendered(true);
      }, splashTimeLeft);

      return () => clearTimeout(timer);
    } else {
      //logd("without timer");
      setLoaded(true);
      setPageRendered(true);
    }
  }

  function end() {
    const endTime = performance.now();
    let timeDiff = endTime - startTime; //in ms
    // strip the ms
    timeDiff /= 1000;

    // get seconds
    const seconds = Math.round(timeDiff);
    //logd(seconds + " seconds");
    return seconds;
  }

  function handleAnimationDisabled(state) {
    logd("isNowDisabled:" + isAnimationDisabled);
    setAnimationDisabled(state);
    setAnimationStateDisabled(state);
    logd("after change Disabled:" + MotionGlobalConfig.skipAnimations);
  }

  function setAnimationStateDisabled(state) {
    //disable motion globally
    MotionGlobalConfig.skipAnimations = state;
  }

  const formatTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");
    const milliseconds = now.getMilliseconds().toString().padStart(3, "0");
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  const logd = (...args) => {
    console.log(...args);
    const formattedLine = formatLine(...args);
    setLogText(addLine(formattedLine));
  };

  const loge = (...args) => {
    console.error(...args);
    const formattedLine = formatLine(...args);
    setLogText(addLine(formattedLine, "ERROR"));
  };

  function formatLine(...args) {
    return args.map((arg) => (typeof arg === "object" ? JSON.stringify(arg, null, 2) : arg)).join(" ");
  }

  function addLine(item, type = "DEBUG") {
    const line = (prevText) => `${prevText}[${formatTime()}] ${type}: ${item}\n`;
    return line;
  }

  return (
    <FormContext.Provider
      value={{
        currentBtnPageIndex,
        selectedButtonId,
        validate,
        pageData,
        getPageData,
        getButtonsData,
        navigateNext,
        navigateHome,
        navigateBackButtons,
        navigateNextButtons,
        showBackButton,
        setData,
        handleInput,
        state,
        welcomePageState,
        loaded: loaded,
        isTaskRunning,
        isEmptyState,
        lastError,
        handleInputWithKeyboard,
        navigateNextInsuranceButton,
        isDebugMode,
        handleDebug,
        selectedPageIndex,
        isInErrorState,
        navigateToServicePage,
        handleAnimationDisabled,
        setDarkMode,
        runRestartOrShutdown,
        isAnimationDisabled,
        isDebugModeBackBtnClickable,
        logText,
        logd,
        loge,
        sendLogToApi,
        storageUid,
        stepCount: pageData.length - 1,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}
